import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { Alert, InsightSignalType, Module } from "@bios/types";
import { Card, Pill } from "@bios/ui";
import { useApiAuthed, useApiDev } from "../auth.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

export function Alerts({ devModeNoAuth }: PageProps) {
  const { module = "media" } = useParams<{ module: Module }>();
  const m = module as Module;
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.alerts(m).then((r) => setAlerts(r.alerts)).catch((e) => setError(String(e)));
  }, [api, m]);

  return (
    <>
      <div className="bios-page-header">
        <div>
          <h1 className="bios-page-title">Insights</h1>
          <p className="bios-page-sub">Decision-grade signals — what happened, what it indicates, why it matters.</p>
        </div>
      </div>
      {error && (
        <Card>
          <p style={{ color: "var(--bios-noise)", fontSize: 13 }}>{error}</p>
        </Card>
      )}
      {alerts.length === 0 ? (
        <Card>
          <p className="bios-empty">No active insights.</p>
        </Card>
      ) : (
        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "1fr" }}>
          {alerts.map((a) => (
            <InsightCard key={a.id} alert={a} />
          ))}
        </div>
      )}
    </>
  );
}

const SIGNAL_TYPE_LABEL: Record<string, string> = {
  CAPITAL_FLOW: "Capital flow",
  POLICY_SHIFT: "Policy shift",
  LEGAL_ACTION: "Legal action",
  MEDIA_CONSOLIDATION: "Media consolidation",
  CULTURAL_INFLUENCE: "Cultural influence",
  INSTITUTIONAL_CHANGE: "Institutional change",
  PR_NARRATIVE: "PR / narrative",
  ORIGINAL_REPORTING_SIGNAL: "Original reporting",
  JOB_CREATED: "Job created",
  JOB_LOST: "Job lost",
  PROGRAM_LAUNCH: "Program launch",
  PLACEMENT_RATE: "Placement rate",
  WAGE_CHANGE: "Wage change",
  BUSINESS_FORMATION: "Business formation",
  FUNDING_FOR_WORKFORCE: "Workforce funding",
};

const SIGNAL_TYPE_COLOR: Record<string, string> = {
  CAPITAL_FLOW: "var(--bios-signal)",
  POLICY_SHIFT: "var(--bios-info)",
  LEGAL_ACTION: "var(--bios-mixed)",
  MEDIA_CONSOLIDATION: "var(--bios-info)",
  CULTURAL_INFLUENCE: "var(--bios-accent)",
  INSTITUTIONAL_CHANGE: "var(--bios-signal)",
  PR_NARRATIVE: "var(--bios-noise)",
  ORIGINAL_REPORTING_SIGNAL: "var(--bios-signal)",
  JOB_CREATED: "var(--bios-signal)",
  JOB_LOST: "var(--bios-noise)",
  PROGRAM_LAUNCH: "var(--bios-info)",
  PLACEMENT_RATE: "var(--bios-signal)",
  WAGE_CHANGE: "var(--bios-mixed)",
  BUSINESS_FORMATION: "var(--bios-accent)",
  FUNDING_FOR_WORKFORCE: "var(--bios-info)",
};

export function InsightCard({ alert }: { alert: Alert }) {
  const hasInsight =
    alert.signal_type ||
    alert.what_happened ||
    alert.what_it_indicates ||
    alert.why_it_matters;
  const sigType = alert.signal_type ?? null;
  return (
    <Card>
      <div className="bios-insight-card">
        {sigType && <SignalTypeBadge signal_type={sigType} />}
        <h3 className="bios-insight-title">{alert.title}</h3>
        {hasInsight ? (
          <div className="bios-insight-body">
            {alert.what_happened && (
              <InsightRow label="What happened" value={alert.what_happened} />
            )}
            {alert.what_it_indicates && (
              <InsightRow label="What it indicates" value={alert.what_it_indicates} />
            )}
            {alert.why_it_matters && (
              <InsightRow label="Why it matters" value={alert.why_it_matters} />
            )}
          </div>
        ) : (
          <p className="bios-insight-fallback">{alert.body}</p>
        )}
        <div className="bios-insight-foot">
          <span className="bios-insight-time">
            {new Date(alert.created_at).toLocaleString()}
          </span>
          <Pill
            variant={
              alert.severity === "critical"
                ? "noise"
                : alert.severity === "elevated"
                ? "mixed"
                : "default"
            }
          >
            {alert.severity}
          </Pill>
        </div>
      </div>
    </Card>
  );
}

function SignalTypeBadge({ signal_type }: { signal_type: InsightSignalType | string }) {
  const label = SIGNAL_TYPE_LABEL[signal_type] ?? signal_type;
  const color = SIGNAL_TYPE_COLOR[signal_type] ?? "var(--bios-text-muted)";
  return (
    <span
      className="bios-signal-badge"
      style={{
        color,
        borderColor: color,
      }}
    >
      {label}
    </span>
  );
}

function InsightRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="bios-insight-row">
      <div className="bios-insight-row-label">{label}</div>
      <div className="bios-insight-row-value">{value}</div>
    </div>
  );
}
